import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Cpu, Camera, BarChart3, Wrench, Shield, Zap, Brain, Activity } from 'lucide-react';

const PropertyTech = () => {
  return (
    <div className="min-h-screen pt-20 lg:pt-24">
      <Helmet>
        <title>IT-Powered Real Estate Intelligence | Arthaa Build — AI Property Management</title>
        <meta name="description" content="Arthaa Build IT-powered real estate intelligence — AI facility management, IoT surveillance, property analytics, and data-driven real estate operations. Smart construction technology India." />
        <link rel="canonical" href="https://arthaabuild.com/property-tech" />
      </Helmet>

      {/* Header */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-block px-4 py-2 bg-[#2B5278]/20 border border-[#2B5278]/30 rounded-full mb-6">
              <p className="text-[#7EB3D8] text-sm font-semibold">COMING SOON</p>
            </div>
            <h1 className="font-serif font-bold text-5xl sm:text-6xl mb-6">
              IT-Powered Real Estate Intelligence by Arthaa Build
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Transforming property management with AI-powered analytics, IoT surveillance, and data-driven decision-making for modern real estate operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl text-slate-900 mb-4">The Future of Property Management</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Combining construction expertise with cutting-edge technology to deliver transparent, efficient, and optimized asset management.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Brain className="w-8 h-8" />, title: 'AI Analytics', desc: 'Predictive maintenance, energy optimization, and automated decision-making' },
              { icon: <Camera className="w-8 h-8" />, title: 'IoT Surveillance', desc: 'Real-time monitoring, security alerts, and integrated oversight systems' },
              { icon: <BarChart3 className="w-8 h-8" />, title: 'Data Dashboards', desc: 'Performance metrics, occupancy tracking, and financial analytics' },
              { icon: <Activity className="w-8 h-8" />, title: 'Smart Operations', desc: 'Automated work orders, vendor management, maintenance scheduling' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="luxury-card bg-white p-8 rounded-xl text-center border border-slate-200"
              >
                <div className="w-16 h-16 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mx-auto mb-6">
                  {item.icon}
                </div>
                <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Advantage */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif font-bold text-4xl text-slate-900 mb-6">
                Integrated from Day One
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Unlike standalone property management companies, we bring end-to-end expertise — from precast construction technology to brand leasing to tech-enabled operations.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                This means smarter buildings from the ground up, with surveillance and IoT systems built into our construction projects from day one.
              </p>
              <ul className="space-y-3">
                {['Construction-to-operations continuity', 'Pre-integrated tech infrastructure', 'Tenant relationship management', 'Data-driven asset optimization'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-2 h-2 bg-[#2B5278] rounded-full"></div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070" 
                alt="Property Technology"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <h2 className="font-serif font-bold text-4xl mb-6">Be First to Know</h2>
          <p className="text-xl text-white/90 mb-8">Get updates when our AI-powered property management solutions launch</p>
          <Link to="/contact" className="inline-block bg-white text-[#2B5278] hover:bg-slate-50 font-semibold px-10 py-4 rounded-md transition-all">
            Express Interest
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PropertyTech;